import React from 'react';
import { CameraView } from 'expo-camera';
import { useSelector } from 'hooks/useSelector';
import { selectBarCodeScanner, selectGeneric } from 'store/app/selectors';
import { CLOSE_BARCODE_SCANNER, SET_GENERIC } from 'store/app/actions';
import { useDispatch } from 'react-redux';
import { View } from 'react-native';
import { theme } from 'lib/theme';
import { Txt } from 'components/Txt';
import { Icon, ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { useEsc } from 'hooks/useEsc';

export const QRScannerScreen: React.FC<
  ModalScreenProps<ModalScreens.QR_SCANNER>
> = () => {
  const dispatch = useDispatch();
  const { bottom } = useSafeAreaInsets();
  const barCodeScanner = useSelector(selectBarCodeScanner);
  const flashlight = useSelector((state) =>
    selectGeneric(state, 'qrCodeScannerFlashlight'),
  ) as undefined | true;
  const instanceRef = React.useRef(0);
  const foundRef = React.useRef(false);

  const handleBarCodeScanned = React.useCallback(
    ({ data }: { data: string }) => {
      if (!foundRef.current) {
        foundRef.current = true;
        dispatch(
          CLOSE_BARCODE_SCANNER({
            data,
          }),
        );
      }
    },
    [dispatch],
  );

  React.useEffect(() => {
    if (!barCodeScanner) {
      instanceRef.current = 0;
      foundRef.current = false;
    } else if (instanceRef.current !== barCodeScanner.instance) {
      instanceRef.current = barCodeScanner.instance;
      foundRef.current = false;
    }
  }, [barCodeScanner]);

  const handleCancel = () => {
    if (!foundRef.current) {
      foundRef.current = true;
      dispatch(CLOSE_BARCODE_SCANNER({ data: '' }));
    }
  };

  useEsc(handleCancel);

  if (!barCodeScanner) {
    return null;
  }

  const toggleFlashlight = () => {
    dispatch(
      SET_GENERIC({
        key: 'qrCodeScannerFlashlight',
        value: flashlight ? undefined : true,
      }),
    );
  };

  return (
    <>
      <CameraView
        style={{ flex: 1, height: '100%' }}
        onBarcodeScanned={!foundRef.current ? handleBarCodeScanned : undefined}
        barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
        enableTorch={!!flashlight}
      />
      {!!barCodeScanner.text && (
        <View
          style={{
            position: 'absolute',
            alignSelf: 'center',
            top: 0,
            margin: theme.paddingPageVertical * 2,
            backgroundColor: 'rgba(0,0,0,.42)',
            borderRadius: 10,
            paddingHorizontal: theme.paddingHorizontal * 2,
            paddingVertical: theme.paddingHorizontal,
            zIndex: 1,
          }}
        >
          <Txt
            style={{
              textShadowColor: 'black',
              textShadowOffset: { width: 0, height: 0 },
              textShadowRadius: 10,
              color: 'white',
              flex: 1,
              textAlign: 'center',
            }}
            numberOfLines={2}
          >
            {barCodeScanner.text}
          </Txt>
        </View>
      )}
      <View
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,.42)',
          paddingHorizontal: theme.paddingPageVertical * 2,
          paddingTop: theme.paddingPageVertical * 2,
          paddingBottom: theme.paddingPageVertical * 2 + bottom,
          zIndex: 1,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            icon={ICONS.FlashlightLight}
            onPress={toggleFlashlight}
            color={flashlight ? 'black' : 'white'}
            size={52}
            iconSize={26}
            imageStyle={{
              transform: [{ rotate: '-90deg' }],
            }}
            style={{
              shadowColor: flashlight ? 'white' : 'black',
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.5,
              shadowRadius: 10,
              backgroundColor: flashlight
                ? 'rgba(255,255,255,0.44)'
                : 'rgba(0,0,0,0.44)',
            }}
          />
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
            }}
          >
            <Icon
              source={ICONS.QRCodeLight}
              color="white"
              size={36}
              style={{
                shadowColor: 'black',
                shadowOffset: { width: 0, height: 0 },
                shadowRadius: 5,
                shadowOpacity: 0.8,
                marginRight: theme.paddingHorizontal,
              }}
            />
            <Txt
              style={{
                textShadowColor: 'black',
                textShadowOffset: { width: 0, height: 0 },
                textShadowRadius: 10,
                color: 'white',
              }}
              size="large"
            >
              Scan a{'\n'}QR Code
            </Txt>
          </View>
          <IconButton
            icon={ICONS.XmarkLight}
            color="white"
            size={52}
            onPress={handleCancel}
            style={{
              shadowColor: 'black',
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.5,
              shadowRadius: 10,
              backgroundColor: 'rgba(0,0,0,0.44)',
            }}
          />
        </View>
      </View>
    </>
  );
};
